import BaseComponent from '@js/BaseComponent'
import Animator from '@js/Animator'
import { hasMotion, hasTouch } from '@js/mediaQueries'
import { easeInOutQuad } from '@js/easing'

/**
 * Manages the interactive state of Breadcrumb nav components
 *
 * @extends BaseComponent
 */
class Breadcrumb extends BaseComponent {
    animator
    lastAnimValue
    hoverElem
    focusElem

    constructor(element) {
        super(element)

        this.activeEntries = [...this.element.querySelectorAll('.breadcrumb-entry[href]:not([href=""])')]

        const elemStyle = getComputedStyle(this.element)
        this.offsetTop =    elemStyle.getPropertyValue('--jiggle-anim-top')
        this.offsetRight =  elemStyle.getPropertyValue('--jiggle-anim-right')
        this.offsetBottom = elemStyle.getPropertyValue('--jiggle-anim-bottom')
        this.offsetLeft =   elemStyle.getPropertyValue('--jiggle-anim-left')

        this.element.addEventListener('focusin', this.onFocusIn)
        this.element.addEventListener('focusout', this.onFocusOut)
        this.element.addEventListener('mouseover', () => {
            this.element.addEventListener('mousemove', this.onMouseMove)
        })
        this.element.addEventListener('mouseleave', () => {
            this.element.removeEventListener('mousemove', this.onMouseMove)
            this.hoverElem = null
            this.element.classList.remove('focus--mouse')
            if (!this.hoverElem && !this.focusElem) {
                this.element.classList.remove('focus')
            }
        })
    }

    /**
     * Runs on focusin and updates the focus element and parent classes
     * @param {FocusEvent}
     */
    onFocusIn = ({ target, relatedTarget }) => {
        if (hasTouch()) return

        const hasFocus = this.element.classList.contains('focus')
        if (!this.element.contains(relatedTarget)) {
            this.element.classList.add('focus', 'focus--keyboard')
        }

        this.focusElem = target
        this.updateTarget(this.focusElem, hasFocus)
    }

    /**
     * Runs on focusout and updates the focus element and parent classes
     * @param {FocusEvent}
     */
    onFocusOut = ({ relatedTarget }) => {
        if (hasTouch()) return

        if (!this.element.contains(relatedTarget)) {
            this.element.classList.remove('focus--keyboard')
            this.focusElem = null

            if (!this.hoverElem && !this.focusElem) {
                this.element.classList.remove('focus')
            }
        }
    }

    /**
     * Runs on mousemove and updates the hover element and parent classes
     * @param {MouseEvent}
     */
    onMouseMove = ({ target }) => {
        const hasFocus = this.element.classList.contains('focus')
        if (!this.activeEntries.includes(target)) {
            this.hoverElem = null
            this.element.classList.remove('focus--mouse')
            if (!this.hoverElem && !this.focusElem) {
                this.element.classList.remove('focus')
            }
        } else if (target !== this.hoverElem) {
            if (!this.hoverElem) {
                this.element.classList.add('focus', 'focus--mouse')
            }

            this.hoverElem = target
            this.updateTarget(this.hoverElem, hasFocus)
        }
    }

    /**
     * Calculates right-inset values for the effect background and passes them
     * to the setRightValue method while managing animator objects.
     * @param {Element} target
     * @param {boolean} animate
     */
    updateTarget(target, animate = false) {
        const { right: parentRight } = this.element.querySelector('.breadcrumb-list').getBoundingClientRect()
        const { right: childRight } = target.closest('.breadcrumb-item').getBoundingClientRect()

        if (!animate || this.lastAnimValue === undefined || !hasMotion()) {
            this.setRightValue(parentRight - childRight)
        } else {
            if (this.animator?.isActive) {
                this.animator.cancel()
            }

            this.animator = new Animator(
                this.lastAnimValue,
                parentRight - childRight,
                (value) => {
                    this.setRightValue(value)
                }
            )
            this.animator.easing = easeInOutQuad
            this.animator.start()
        }
    }

    /**
     * Sets the --jiggle-anim-right CSS variable for the Breadcrumb
     * @param {Number} value
     */
    setRightValue(value) {
        this.lastAnimValue = value
        this.element.style.setProperty('--jiggle-anim-right', `calc(${value}px + ${this.offsetRight})`)
    }
}

Breadcrumb.create('.breadcrumb')
